import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProducts, AvailabilityFilter } from '../hooks/useProducts';
import { getTags } from '../services/productService';
import ProductCard from '../components/ProductCard';
import './styles/ProductsPage.css';

const ProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || undefined;
  const search = searchParams.get('search') || undefined;
  const tagsParam = searchParams.get('tags') || '';
  const availabilityParam = searchParams.get('availability') as AvailabilityFilter || 'all';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '12', 10);
  
  // Parse tags from URL
  const selectedTags = tagsParam ? tagsParam.split(',').filter(t => t.trim()) : [];
  
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  
  // Fetch products with availability filter
  // Backend automatically sorts out-of-stock items to the end
  const { products, pagination, loading, error } = useProducts({ 
    category, 
    search, 
    page, 
    limit, 
    tags: selectedTags,
    availability: availabilityParam
  });

  // Fetch available tags
  useEffect(() => {
    async function fetchTags() {
      try {
        const tags = await getTags();
        setAvailableTags(tags);
        setTagsLoading(false);
      } catch (err) {
        console.error('Failed to fetch tags:', err);
        setTagsLoading(false);
      }
    }
    fetchTags();
  }, []);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1'); // Reset to first page
    setSearchParams(params);
  };

  const handleTagToggle = (tag: string) => {
    const params = new URLSearchParams(searchParams);
    let tags = selectedTags.slice();
    
    if (tags.includes(tag)) {
      // Remove tag
      tags = tags.filter(t => t !== tag);
    } else {
      // Add tag
      tags.push(tag);
    }
    
    if (tags.length > 0) {
      params.set('tags', tags.join(','));
    } else {
      params.delete('tags');
    }
    
    params.set('page', '1'); // Reset to first page
    setSearchParams(params);
  };

  const handleAvailabilityChange = (availability: AvailabilityFilter) => {
    const params = new URLSearchParams(searchParams);
    
    if (availability === 'all') {
      params.delete('availability');
    } else {
      params.set('availability', availability);
    }
    
    params.set('page', '1'); // Reset to first page
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('tags');
    params.delete('availability');
    params.set('page', '1');
    setSearchParams(params);
  };

  const hasActiveFilters = selectedTags.length > 0 || availabilityParam !== 'all';

  // Loading state
  if (loading) {
    return (
      <div className="products-page">
        <div className="products-header">
          <div className="skeleton skeleton-title"></div>
          <div className="skeleton skeleton-subtitle"></div>
        </div>

        <div className="products-controls">
          <div className="skeleton skeleton-per-page"></div>
        </div>

        <div className="products-grid">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
            <div key={i} className="product-card skeleton-card">
              <div className="skeleton skeleton-image"></div>
              <div className="product-content">
                <div className="skeleton skeleton-category"></div>
                <div className="skeleton skeleton-name"></div>
                <div className="skeleton skeleton-description"></div>
                <div className="skeleton skeleton-description short"></div>
                <div className="product-footer">
                  <div className="skeleton skeleton-price"></div>
                  <div className="skeleton skeleton-button"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="products-page">
        <div className="error-container">
          <p className="error-message">Failed to load products: {error}</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (products.length === 0) {
    return (
      <div className="products-page">
        <div className="products-header">
          <h1 className="products-title font-serif">
            {search ? `Search Results` : category || 'All Products'}
          </h1>
          <p className="products-subtitle">
            {search ? `No results found for "${search}"` : 'No products available'}
          </p>
        </div>

        {/* Filters - still show in empty state so users can change filters */}
        <div className="filters-section">
          {/* Availability Filter */}
          <div className="availability-filter">
            <h3 className="filter-title">Availability:</h3>
            <div className="availability-buttons">
              <button
                className={`availability-button ${availabilityParam === 'all' ? 'active' : ''}`}
                onClick={() => handleAvailabilityChange('all')}
              >
                All
              </button>
              <button
                className={`availability-button ${availabilityParam === 'in-stock' ? 'active' : ''}`}
                onClick={() => handleAvailabilityChange('in-stock')}
              >
                <svg className="availability-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                In Stock
              </button>
              <button
                className={`availability-button ${availabilityParam === 'out-of-stock' ? 'active' : ''}`}
                onClick={() => handleAvailabilityChange('out-of-stock')}
              >
                <svg className="availability-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Out of Stock
              </button>
            </div>
          </div>

          {/* Tag Filters */}
          {!tagsLoading && availableTags.length > 0 && (
            <div className="tag-filters">
              <div className="tag-filters-header">
                <h3 className="filter-title">Filter by Tags:</h3>
                {hasActiveFilters && (
                  <button className="clear-filters-button" onClick={handleClearFilters}>
                    <svg className="clear-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear Filters
                  </button>
                )}
              </div>
              <div className="tag-list">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    className={`tag-button ${selectedTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {selectedTags.includes(tag) && (
                      <svg className="tag-check" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="empty-state">
          <p>Try adjusting your filters or search terms.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* Header */}
      <div className="products-header">
        <h1 className="products-title font-serif">
          {search ? `Search Results` : category || 'All Products'}
        </h1>
        <p className="products-subtitle">
          {search ? (
            <>Showing results for <span className="search-term">"{search}"</span></>
          ) : category ? (
            `Browse our ${category.toLowerCase()} collection`
          ) : (
            'Explore our complete collection of handcrafted items'
          )}
        </p>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        {/* Availability Filter */}
        <div className="availability-filter">
          <h3 className="filter-title">Availability:</h3>
          <div className="availability-buttons">
            <button
              className={`availability-button ${availabilityParam === 'all' ? 'active' : ''}`}
              onClick={() => handleAvailabilityChange('all')}
            >
              All
            </button>
            <button
              className={`availability-button ${availabilityParam === 'in-stock' ? 'active' : ''}`}
              onClick={() => handleAvailabilityChange('in-stock')}
            >
              <svg className="availability-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              In Stock
            </button>
            <button
              className={`availability-button ${availabilityParam === 'out-of-stock' ? 'active' : ''}`}
              onClick={() => handleAvailabilityChange('out-of-stock')}
            >
              <svg className="availability-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Out of Stock
            </button>
          </div>
        </div>

        {/* Tag Filters */}
        {!tagsLoading && availableTags.length > 0 && (
          <div className="tag-filters">
            <div className="tag-filters-header">
              <h3 className="filter-title">Filter by Tags:</h3>
              {hasActiveFilters && (
                <button className="clear-filters-button" onClick={handleClearFilters}>
                  <svg className="clear-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear Filters
                </button>
              )}
            </div>
            <div className="tag-list">
              {availableTags.map((tag) => (
                <button
                  key={tag}
                  className={`tag-button ${selectedTags.includes(tag) ? 'active' : ''}`}
                  onClick={() => handleTagToggle(tag)}
                >
                  {selectedTags.includes(tag) && (
                    <svg className="tag-check" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="products-controls">
        <div className="per-page-selector">
          <label htmlFor="per-page">Products per page:</label>
          <select
            id="per-page"
            value={limit}
            onChange={(e) => handleLimitChange(parseInt(e.target.value, 10))}
          >
            <option value="6">6</option>
            <option value="12">12</option>
            <option value="24">24</option>
            <option value="48">48</option>
          </select>
        </div>

        <div className="results-info">
          Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, pagination?.total || 0)} of {pagination?.total || 0}
        </div>
      </div>

      {/* Products Grid - Now using ProductCard component */}
      <div className="products-grid">
        {products.map((product) => (
          <ProductCard 
            key={product.id} 
            product={product} 
            variant="grid"
            descriptionLines={2}
          />
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(page - 1)}
            disabled={!pagination.hasPrev}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div className="pagination-pages">
            {/* First page */}
            {page > 3 && (
              <>
                <button className="pagination-page" onClick={() => handlePageChange(1)}>
                  1
                </button>
                {page > 4 && <span className="pagination-ellipsis">...</span>}
              </>
            )}

            {/* Pages around current */}
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(p => p >= page - 2 && p <= page + 2)
              .map(p => (
                <button
                  key={p}
                  className={`pagination-page ${p === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              ))}

            {/* Last page */}
            {page < pagination.totalPages - 2 && (
              <>
                {page < pagination.totalPages - 3 && <span className="pagination-ellipsis">...</span>}
                <button className="pagination-page" onClick={() => handlePageChange(pagination.totalPages)}>
                  {pagination.totalPages}
                </button>
              </>
            )}
          </div>

          <button
            className="pagination-button"
            onClick={() => handlePageChange(page + 1)}
            disabled={!pagination.hasNext}
          >
            Next
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      {/* Simple Navigation Footer */}
      {pagination && (
        <div className="pagination-footer">
          <button
            className="pagination-nav-button"
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
            title="First page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          <button
            className="pagination-nav-button"
            onClick={() => handlePageChange(page - 1)}
            disabled={!pagination.hasPrev}
            title="Previous page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <span className="pagination-current">
            Page {page} of {pagination.totalPages}
          </span>

          <button
            className="pagination-nav-button"
            onClick={() => handlePageChange(page + 1)}
            disabled={!pagination.hasNext}
            title="Next page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            className="pagination-nav-button"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={page === pagination.totalPages}
            title="Last page"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;